import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import withStyles from '@mui/styles/withStyles';
import { serverUrl } from '../../environment';
import ValidationPlaceholder from './ValidationPlaceholder';

export function applyShortcodes(description, substitutionFields) {
    if (substitutionFields) {
        for (let key in substitutionFields) {
            // legacy is just [key] so check for [[key]] then [key]
            const keyTwo = '[' === key.substring(1, 2) ? key : '[' + key + ']';
            const keyOne = '[' !== key.substring(1, 2) ? key : key.substring(1, key.length - 1);
            const keyReg = keyOne.replace(/([[\]])/g, '\\$1');
            const textRegex = new RegExp(`([^[]|^)${keyReg}([^\\]]|$)`, 'gm');
            description = description.replace(textRegex, `$1${keyTwo}$2`);
            if (substitutionFields[key]) {
                description = description.split(keyTwo).join(substitutionFields[key]);
            }
        }
    }
    return description;
}

const applyMissingTags = loadedValue => {
    if (-1 === loadedValue.search(/^\s*?</)) {
        loadedValue = '<p>' + loadedValue.replace(/(\r?\n){2}/g, '</p><p>') + '</p>';
        loadedValue = loadedValue.replace(/\r?\n/g, '<br/>');
        loadedValue = loadedValue.replace(/<p><\/p>/g, '');
    }
    return loadedValue;
};

const stripMergeTags = wasContent => {
    return wasContent.replace(/<span contenteditable="false">\[\[(.+?)]]<\/span>/gim, '[[$1]]');
};
const addMergeTags = newContent => {
    return stripMergeTags(newContent).replace(/\[\[(.+?)]]/gim, '<span contenteditable="false">[[$1]]</span>');
};

function HtmlEditor({
    classes,
    name,
    form,
    label = '',
    shortcodes = null,
    height = 250,
    required = false,
    disabled = false,
    readOnly = false
}) {
    const editorRef = useRef(null);
    if (!(!!form && !!name)) return null;
    const scriptsSrc = [
        `${serverUrl}/_resources/vendor/silverstripe/admin/client/dist/tinymce/tinymce.js` /* import TinyMCE6 from SS5 */
    ];
    const initialValue = addMergeTags(applyMissingTags(form.getOriginal(name) || ''));
    const noEdit = !!(disabled || readOnly);
    const value = addMergeTags(applyMissingTags(form.getField(name) || ''));
    const safeName = JSON.stringify(name);

    const element = (
        <div id={'editor_container_' + safeName}>
            {label && (
                <span className={classes.labelStyle}>
                    {label}
                    {required && ' *'}
                </span>
            )}
            <Editor
                key={
                    noEdit ? safeName + 'noEdit' : safeName
                } /* required: this does a reset on the editor config when noEdit changes */
                tinymceScriptSrc={scriptsSrc}
                licenseKey="gpl"
                id={'editor_' + safeName}
                initialValue={initialValue}
                onInit={(_evt, editor) => (editorRef.current = editor)}
                onEditorChange={!noEdit ? newValue => form.setField({ [name]: stripMergeTags(newValue) }) : undefined}
                value={value}
                //disabled={noEdit} /* removed: it disables the toolbar & content selection, so not using it! */
                init={{
                    skin: 'silverstripe',
                    toolbar_mode: 'wrap',
                    toolbar_sticky: false,
                    ui_mode: 'split',
                    promotion: false,
                    branding: false,
                    menubar: false,
                    height,
                    plugins: [
                        'advlist',
                        'autolink',
                        'lists',
                        'link',
                        'image',
                        'charmap',
                        // 'contexttoolbar',
                        'anchor',
                        'searchreplace',
                        'code',
                        'fullscreen',
                        // 'hr',
                        'media',
                        'table',
                        'code',
                        'help',
                        // 'print',
                        'preview',
                        'wordcount'
                    ],
                    toolbar: !noEdit
                        ? 'fullscreen searchreplace | bold italic underline removeformat | align | numlist bullist' +
                          ' | outdent indent | insert shortcodes table | printlabel code'
                        : 'fullscreen | copy | printlabel',
                    contextmenu: !!noEdit ? 'copy' : 'undo redo | cut copy pastebutton | insert shortcodes',
                    link_context_toolbar: true,
                    browser_spellcheck: true,
                    content_style:
                        'body { font-family:Roboto,Helvetica,Arial,sans-serif; font-size:1rem; max-width: 660px; margin-right: auto; padding: 0 6px; }' +
                        ' span[contenteditable=false] { background: #b4d7ff33; cursor: default!important; }' +
                        ' span[contenteditable=false]:hover { background: #006ce733; }',
                    setup: function(editor) {
                        editor.on('PostRender', function() {
                            /* required: prevent updates to the content area, but allow selections */
                            if (!!noEdit) {
                                editor.getBody().setAttribute('contenteditable', false);
                            }
                        });
                        editor.on('SetContent', function() {
                            /* required: send any click on links to new window */
                            if (!!noEdit) {
                                const links = editor.getBody().getElementsByTagName('a');
                                for (let i = 0; i < links.length; i++) {
                                    links[i].setAttribute('target', '_blank');
                                }
                            }
                        });

                        editor.ui.registry.addButton('printlabel', {
                            icon: 'print',
                            text: 'Print',
                            tooltip: 'Print',
                            onAction: () => editor.execCommand('mcePrint')
                        });

                        if (!!noEdit) {
                            return;
                        }

                        editor.ui.registry.addContextToolbar('imagealignment', {
                            predicate: node => node.nodeName.toLowerCase() === 'img',
                            items: 'alignleft aligncenter alignright image link',
                            position: 'node',
                            scope: 'node'
                        });
                        editor.ui.registry.addContextToolbar('link', {
                            predicate: node => node.nodeName.toLowerCase() === 'a' && !!node.getAttribute('id'),
                            items: 'anchor',
                            position: 'selection',
                            scope: 'node'
                        });
                        editor.ui.registry.addMenuItem('pastebutton', {
                            text: 'Paste',
                            icon: 'paste',
                            onAction: async () => {
                                try {
                                    const text = await navigator.clipboard.readText();
                                    editor.insertContent(text);
                                } catch (error) {
                                    console.warn('Failed to read clipboard');
                                }
                            }
                        });
                        const insertItems = [
                            {
                                type: 'menuitem',
                                icon: 'image',
                                text: 'Image...',
                                onAction: () => {
                                    // Trigger the image dialog provided by the image plugin
                                    editor.execCommand('mceImage');
                                }
                            },
                            {
                                type: 'menuitem',
                                icon: 'link',
                                text: 'Link...',
                                onAction: () => {
                                    // Trigger the image dialog provided by the image plugin
                                    editor.execCommand('mceLink');
                                }
                            },
                            {
                                type: 'menuitem',
                                icon: 'bookmark',
                                text: 'Anchor...',
                                onAction: () => {
                                    // Trigger the image dialog provided by the image plugin
                                    editor.execCommand('mceAnchor');
                                }
                            },
                            {
                                type: 'menuitem',
                                icon: 'insert-character',
                                text: 'Special Character...',
                                onAction: () => {
                                    // Trigger the image dialog provided by the image plugin
                                    editor.execCommand('mceShowCharmap');
                                }
                            },
                            {
                                type: 'menuitem',
                                icon: 'horizontal-rule',
                                text: 'Horizontal Line',
                                onAction: () => {
                                    // Trigger the image dialog provided by the image plugin
                                    editor.execCommand('InsertHorizontalRule');
                                }
                            }
                        ];
                        editor.ui.registry.addMenuButton('insert', {
                            text: 'Insert',
                            tooltip: 'Insert objects',
                            icon: 'plus',
                            fetch: callback => callback(insertItems)
                        });
                        editor.ui.registry.addContextMenu('insert', {
                            update: node => [
                                {
                                    type: 'submenu',
                                    icon: 'plus',
                                    text: 'Insert',
                                    getSubmenuItems: () => insertItems
                                }
                            ]
                        });
                        editor.ui.registry.addButton('pastebutton', {
                            tooltip: 'Paste',
                            icon: 'paste',
                            onAction: async () => {
                                try {
                                    const text = await navigator.clipboard.readText();
                                    editor.insertContent(text);
                                } catch (error) {
                                    console.warn('Failed to read clipboard');
                                }
                            }
                        });

                        if (shortcodes) {
                            const menu = [];
                            for (let key in shortcodes) {
                                menu.push({
                                    type: 'menuitem',
                                    icon: !!shortcodes[key] ? 'checkmark' : null,
                                    text: key,
                                    onAction: () => editor.insertContent(shortcodes[key] || key)
                                });
                            }
                            editor.ui.registry.addMenuButton('shortcodes', {
                                icon: 'addtag',
                                text: 'Merge tags',
                                tooltip: 'Insert data',
                                fetch: callback => callback(menu)
                            });
                            editor.ui.registry.addContextMenu('shortcodes', {
                                update: node => [
                                    {
                                        type: 'submenu',
                                        icon: 'addtag',
                                        text: 'Merge tags',
                                        getSubmenuItems: () => menu
                                    }
                                ]
                            });
                        }
                    },
                    images_upload_handler: (blob, progress) =>
                        new Promise((resolve, reject) => {
                            const img = 'data:image/jpeg;base64,' + blob.base64();
                            progress(100);
                            if (img.length > 1350000) reject({ message: 'Image is too big, keep under 1MB' });
                            else resolve(img);
                        }),
                    init_instance_callback: function(editor) {
                        let preProcessInnerHtml;

                        // Event fires *before* the command runs (preview/print modal is about to open)
                        editor.on('BeforeExecCommand', function(e) {
                            if (['mcePreview', 'mcePrint'].includes(e.command)) {
                                // 1. Store the original content
                                preProcessInnerHtml = editor.getContent();

                                // 2. Modify the content in the editor (for the modal to use)
                                var modifiedContent = applyShortcodes(stripMergeTags(preProcessInnerHtml), shortcodes);
                                editor.setContent(modifiedContent);
                            }
                        });

                        // Event fires *after* the command runs (modal is open)
                        editor.on('ExecCommand', function(e) {
                            if (['mcePreview', 'mcePrint'].includes(e.command)) {
                                // 3. Immediately restore the original content in the editor
                                editor.setContent(preProcessInnerHtml);
                            }
                        });
                    }
                }}
            />
        </div>
    );

    return (
        <ValidationPlaceholder form={form} name={name}>
            {element}
        </ValidationPlaceholder>
    );
}

const styles = ({ palette }) => ({
    labelStyle: {
        color: 'white',
        cursor: 'pointer',
        padding: '1px 7px',
        borderRadius: '4px',
        backgroundColor: palette.action.active,
        borderBottomLeftRadius: 0,
        textTransform: 'capitalize',
        fontSize: '0.75rem'
    }
});
export default withStyles(styles)(HtmlEditor);
